import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  input,
  InputSignal,
  OnDestroy,
  OnInit,
  SimpleChanges,
  output,
  OutputEmitterRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  trigger,
  state,
  style,
  animate,
  transition,
} from '@angular/animations';

import { PopoverModule } from 'primeng/popover';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import {
  AssignCase,
  DataTableColumnConfigI,
  PDropdownOptions,
  ProccessedCoassessCase,
  ProccessedCoreactCase,
  User,
} from '../../interfaces';
import { DatePipe, DecimalPipe } from '@angular/common';
import { CaseService, IPatchCase } from '../../services';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { Roles, status } from '../../utils';
import { TranslationService } from '../../services/translation.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

type SelectionModeInput = 'auto' | 'enabled' | 'disabled';
type AssignmentModelInput = 'auto' | 'coassess' | 'coreact';
type NavigationConfigInput = {
  detailRoute: string;
  detailIdField: string;
};

@Component({
  selector: 'case-table',
  templateUrl: './case-table.component.html',
  styleUrls: ['./case-table.component.scss'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [
    PopoverModule,
    ButtonModule,
    TableModule,
    SelectModule,
    FormsModule,
    ToastModule,
    DatePipe,
    DecimalPipe,
    TranslatePipe,
  ],
  animations: [
    trigger('datatableAnimation', [
      state(
        'expanded',
        style({
          height: '', // Use the actual height of the element
          opacity: 1,
          overflow: 'hidden',
        })
      ),
      state(
        'collapsed',
        style({
          height: '0', // Collapse the element to zero height
          opacity: 0,
          overflow: 'hidden',
        })
      ),
      transition('expanded <=> collapsed', [
        animate('300ms ease-in-out', style({ height: 'auto !important' })), // Duration and easing of the animation
      ]),
    ]),
  ],
  providers: [MessageService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClaimsTableComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly appTopbarOffsetPx = 80;
  translateService = inject(TranslationService);
  tableColumnsConfig: InputSignal<DataTableColumnConfigI[]> = input.required();
  cases: InputSignal<(ProccessedCoassessCase | ProccessedCoreactCase)[]> =
    input.required();
  columns: InputSignal<{ prop: string; name: string }[]> = input.required();
  tableDetailConfig: InputSignal<{ name: string; prop: string }[]> =
    input.required();
  userRole: InputSignal<string> = input.required();
  caseManagerOptions: InputSignal<PDropdownOptions[]> = input.required();
  investigationOptions: InputSignal<PDropdownOptions[]> = input.required();
  users: InputSignal<User[]> = input.required();
  totalRows: InputSignal<number> = input.required();

  pageNumber: InputSignal<number> = input.required();
  pageSize: InputSignal<number> = input(10);
  sortBy: InputSignal<string> = input('');
  sortDesc: InputSignal<boolean> = input(false);
  defaultSortField: InputSignal<string> = input('announcementDate');
  assignmentModel: InputSignal<AssignmentModelInput> =
    input<AssignmentModelInput>('auto');
  showSelection: InputSignal<boolean> = input(true);
  selectionMode: InputSignal<SelectionModeInput> =
    input<SelectionModeInput>('auto');
  showDateMetadata: InputSignal<boolean> = input(true);
  navigationConfig: InputSignal<NavigationConfigInput | undefined> =
    input<NavigationConfigInput | undefined>(undefined);
  detailRoute: InputSignal<string> = input('case-detail');
  detailIdField: InputSignal<string> = input('caseId');
  onPageChange: OutputEmitterRef<number> = output();
  onSortChange: OutputEmitterRef<{ sortBy: string; sortDesc: boolean }> =
    output();
  onRowsChange: OutputEmitterRef<
    (ProccessedCoassessCase | ProccessedCoreactCase)[]
  > = output();

  cardDescriptionTemplate?: any;

  selected: any = [];
  selectedCaseManagerId: string = '';
  selectedInvestigatorId: string = '';
  roles = Roles;
  rows: (ProccessedCoassessCase | ProccessedCoreactCase)[] = [];
  selectedCaseManagerMap: { [caseId: string]: string } = {};
  selectedInvestigatorMap: { [caseId: string]: string } = {};
  isTableLoading = false;

  private subscriptions: Subscription[] = [];
  private initialized = false;

  private caseService = inject(CaseService);
  private messageService = inject(MessageService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private hostElementRef = inject(ElementRef<HTMLElement>);
  private rowHeightRafId: number | null = null;
  private readonly onWindowResize = () => this.scheduleEqualizeRowHeights();
  @ViewChild('tableScrollWrapper') tableScrollWrapper?: ElementRef<HTMLElement>;
  @ViewChild('bottomScrollbar') bottomScrollbar?: ElementRef<HTMLElement>;
  @ViewChild('floatingHeader') floatingHeader?: ElementRef<HTMLElement>;
  showBottomScrollbar = true;
  showFloatingHeader = false;
  bottomScrollbarContentWidth = 0;
  bottomScrollbarWidth = 0;
  bottomScrollbarLeft = 0;
  private tableHorizontalScroller?: HTMLElement;
  private syncingFromTable = false;
  private syncingFromBottom = false;
  private readonly onTableHorizontalScroll = () => {
    if (!this.bottomScrollbar?.nativeElement || this.syncingFromBottom) {
      this.syncFloatingHeaderScroll();
      return;
    }

    this.syncingFromTable = true;
    this.bottomScrollbar.nativeElement.scrollLeft =
      this.tableHorizontalScroller?.scrollLeft ?? 0;
    this.syncingFromTable = false;
    this.syncFloatingHeaderScroll();
  };
  private readonly onWindowResizeForBottomScrollbar = () =>
    this.updateBottomScrollbarState();
  private readonly onWindowScrollForBottomScrollbar = () =>
    this.updateBottomScrollbarState();

  ngAfterViewInit(): void {
    window.addEventListener('resize', this.onWindowResize);
    window.addEventListener('resize', this.onWindowResizeForBottomScrollbar);
    window.addEventListener('scroll', this.onWindowScrollForBottomScrollbar, {
      passive: true,
    });
    this.bindBottomScrollbar();
    queueMicrotask(() => this.cdr.detectChanges());
    this.scheduleEqualizeRowHeights();
    this.updateBottomScrollbarState();
  }
  constructor(private translate: TranslateService) {}

  ngOnInit(): void {}
  ngOnDestroy(): void {
    window.removeEventListener('resize', this.onWindowResize);
    window.removeEventListener('resize', this.onWindowResizeForBottomScrollbar);
    window.removeEventListener('scroll', this.onWindowScrollForBottomScrollbar);
    this.unbindBottomScrollbar();
    if (this.rowHeightRafId !== null) {
      cancelAnimationFrame(this.rowHeightRafId);
      this.rowHeightRafId = null;
    }
  }
  ngOnChanges(changes: SimpleChanges) {
    this.rows = this.cases();
    if (changes['cases']) {
      this.isTableLoading = false;
      this.scheduleEqualizeRowHeights();
      queueMicrotask(() => {
        this.bindBottomScrollbar();
        this.updateBottomScrollbarState();
        this.updateFloatingHeaderState();
      });
    }
    this.cdr.markForCheck();
  }

  private buildFloatingHeader(): void {
    const floatingHeaderEl = this.floatingHeader?.nativeElement;
    const wrapper = this.tableScrollWrapper?.nativeElement;
    const tableScroller = this.tableHorizontalScroller;
    if (!floatingHeaderEl || !wrapper || !tableScroller) {
      return;
    }

    const sourceTable = wrapper.querySelector(
      '.p-datatable-table'
    ) as HTMLTableElement | null;
    const sourceHead = sourceTable?.querySelector('thead');

    if (!sourceTable || !sourceHead) {
      floatingHeaderEl.innerHTML = '';
      return;
    }

    const clonedTable = document.createElement('table');
    clonedTable.className = sourceTable.className;
    clonedTable.style.width = `${Math.round(sourceTable.getBoundingClientRect().width)}px`;
    clonedTable.appendChild(sourceHead.cloneNode(true));

    floatingHeaderEl.innerHTML = '';
    floatingHeaderEl.appendChild(clonedTable);
    this.syncFloatingHeaderScroll();
  }

  private syncFloatingHeaderScroll(): void {
    const floatingHeaderEl = this.floatingHeader?.nativeElement;
    const tableScroller = this.tableHorizontalScroller;
    if (!floatingHeaderEl || !tableScroller) {
      return;
    }

    const clonedTable = floatingHeaderEl.querySelector('table') as HTMLElement | null;
    if (!clonedTable) {
      return;
    }

    clonedTable.style.transform = `translateX(${-tableScroller.scrollLeft}px)`;
  }

  private updateFloatingHeaderState(): void {
    const wrapper = this.tableScrollWrapper?.nativeElement;
    const floatingHeaderEl = this.floatingHeader?.nativeElement;
    const tableScroller = this.tableHorizontalScroller;
    if (!wrapper || !floatingHeaderEl || !tableScroller) {
      this.showFloatingHeader = false;
      this.cdr.markForCheck();
      return;
    }

    const wrapperRect = wrapper.getBoundingClientRect();
    const scrollerRect = tableScroller.getBoundingClientRect();
    const headerHeight = 56;
    const shouldShow =
      wrapperRect.top <= this.appTopbarOffsetPx - 1 &&
      wrapperRect.bottom > this.appTopbarOffsetPx + headerHeight + 4;

    this.showFloatingHeader = shouldShow;

    if (!shouldShow) {
      floatingHeaderEl.innerHTML = '';
      this.cdr.markForCheck();
      return;
    }

    floatingHeaderEl.style.left = `${Math.round(scrollerRect.left)}px`;
    floatingHeaderEl.style.width = `${Math.round(scrollerRect.width)}px`;
    this.buildFloatingHeader();
    this.cdr.markForCheck();
  }

  private bindBottomScrollbar(): void {
    const wrapper = this.tableScrollWrapper?.nativeElement;
    const tableScroller = wrapper?.querySelector(
      '.p-datatable-table-container, .p-datatable-wrapper'
    ) as HTMLElement | null;

    if (this.tableHorizontalScroller === tableScroller) {
      return;
    }

    this.unbindBottomScrollbar();

    if (!tableScroller) {
      this.showBottomScrollbar = false;
      this.bottomScrollbarContentWidth = 0;
      this.cdr.markForCheck();
      return;
    }

    this.tableHorizontalScroller = tableScroller;
    this.tableHorizontalScroller.addEventListener(
      'scroll',
      this.onTableHorizontalScroll,
      { passive: true }
    );
  }

  private unbindBottomScrollbar(): void {
    if (!this.tableHorizontalScroller) {
      return;
    }

    this.tableHorizontalScroller.removeEventListener(
      'scroll',
      this.onTableHorizontalScroll
    );
    this.tableHorizontalScroller = undefined;
  }

  private updateBottomScrollbarState(): void {
    const tableScroller = this.tableHorizontalScroller;
    if (!tableScroller) {
      this.showBottomScrollbar = false;
      this.bottomScrollbarContentWidth = 0;
      this.bottomScrollbarWidth = 0;
      this.bottomScrollbarLeft = 0;
      this.showFloatingHeader = false;
      this.cdr.markForCheck();
      return;
    }

    const scrollerRect = tableScroller.getBoundingClientRect();

    this.bottomScrollbarContentWidth = Math.max(
      tableScroller.scrollWidth,
      tableScroller.clientWidth + 1
    );
    this.bottomScrollbarWidth = Math.max(0, Math.round(scrollerRect.width));
    this.bottomScrollbarLeft = Math.max(0, Math.round(scrollerRect.left));
    const hasHorizontalOverflow = tableScroller.scrollWidth > tableScroller.clientWidth;
    const viewportBottom =
      window.innerHeight || document.documentElement.clientHeight || 0;
    const nativeScrollbarVisibleInViewport =
      scrollerRect.bottom <= viewportBottom && scrollerRect.top < viewportBottom;

    this.showBottomScrollbar =
      hasHorizontalOverflow && !nativeScrollbarVisibleInViewport;

    if (this.bottomScrollbar?.nativeElement) {
      this.bottomScrollbar.nativeElement.scrollLeft = tableScroller.scrollLeft;
    }

    this.updateFloatingHeaderState();

    this.cdr.markForCheck();
  }

  onBottomScrollbarScroll(): void {
    const tableScroller = this.tableHorizontalScroller;
    const bottomScroller = this.bottomScrollbar?.nativeElement;

    if (!tableScroller || !bottomScroller || this.syncingFromTable) {
      return;
    }

    this.syncingFromBottom = true;
    tableScroller.scrollLeft = bottomScroller.scrollLeft;
    this.syncingFromBottom = false;
  }

  private scheduleEqualizeRowHeights(): void {
    if (this.rowHeightRafId !== null) {
      cancelAnimationFrame(this.rowHeightRafId);
    }

    this.rowHeightRafId = requestAnimationFrame(() => {
      this.rowHeightRafId = null;
      this.equalizeVisibleRowHeights();
    });
  }

  private equalizeVisibleRowHeights(): void {
    const tableRoot = this.hostElementRef.nativeElement;
    const rowNodes = Array.from(
      tableRoot.querySelectorAll(
        '.sticky-header-table .p-datatable-tbody > tr.case-data-row'
      )
    );
    const rows = rowNodes.filter(
      (node): node is HTMLTableRowElement => node instanceof HTMLTableRowElement
    );

    if (!rows.length) {
      return;
    }

    for (const row of rows) {
      row.style.height = '';
      const cellNodes = Array.from(row.querySelectorAll('td'));
      const cells = cellNodes.filter(
        (node): node is HTMLTableCellElement =>
          node instanceof HTMLTableCellElement
      );
      for (const cell of cells) {
        cell.style.height = '';
        cell.style.minHeight = '';
        cell.style.maxHeight = '';
      }
    }

    const maxHeight = Math.max(
      ...rows.map((row) => Math.ceil(row.getBoundingClientRect().height))
    );

    if (!Number.isFinite(maxHeight) || maxHeight <= 0) {
      return;
    }

    const targetHeight = `${maxHeight}px`;
    for (const row of rows) {
      row.style.height = targetHeight;
      const cellNodes = Array.from(row.querySelectorAll('td'));
      const cells = cellNodes.filter(
        (node): node is HTMLTableCellElement =>
          node instanceof HTMLTableCellElement
      );
      for (const cell of cells) {
        cell.style.height = targetHeight;
        cell.style.minHeight = targetHeight;
        cell.style.maxHeight = targetHeight;
      }
    }
  }

  private hasAdjusterField(row: any): boolean {
    return !!row && Object.prototype.hasOwnProperty.call(row, 'adjuster');
  }

  getPrimaryAssigneeLabelKey(row: any): string {
    return this.hasAdjusterField(row)
      ? 'CLAIMS.INVESTIGATION.ADJUSTER'
      : 'CLAIMS.INVESTIGATION.CLAIM_MANAGER';
  }

  getPrimaryAssigneeValue(row: any): string {
    if (this.hasAdjusterField(row)) {
      return row?.adjuster ?? '-';
    }

    return row?.caseManager ?? this.translate.instant('CASE_DETAIL.Unassigned');
  }

  getEffectiveSortField(): string {
    const currentSortBy = this.sortBy();
    if (currentSortBy) {
      return currentSortBy;
    }

    const hasDefaultSortColumn = this.tableColumnsConfig().some(
      (column) => column.prop === this.defaultSortField()
    );

    return hasDefaultSortColumn ? this.defaultSortField() : '';
  }

  getEffectiveSortOrder(): number {
    if (this.sortBy()) {
      return this.sortDesc() ? -1 : 1;
    }

    return this.getEffectiveSortField() ? -1 : 0;
  }

  private hasAssignmentColumns(): boolean {
    return this.columns().some(
      (column) =>
        column.prop === 'caseManager' || column.prop === 'investigator'
    );
  }

  private usesAdjusterAssignmentModel(): boolean {
    return this.columns().some((column) => column.prop === 'adjuster');
  }

  private getEffectiveAssignmentModel(): AssignmentModelInput {
    const assignmentModel = this.assignmentModel();
    if (assignmentModel !== 'auto') {
      return assignmentModel;
    }

    if (this.usesAdjusterAssignmentModel()) {
      return 'coreact';
    }

    if (this.hasAssignmentColumns()) {
      return 'coassess';
    }

    return 'auto';
  }

  private getEffectiveDetailRoute(): string {
    return this.navigationConfig()?.detailRoute ?? this.detailRoute();
  }

  private getEffectiveDetailIdField(): string {
    return this.navigationConfig()?.detailIdField ?? this.detailIdField();
  }

  getTableDataKey(): string {
    return this.getEffectiveDetailIdField();
  }

  getEffectiveShowSelection(): boolean {
    const selectionMode = this.selectionMode();

    if (selectionMode === 'disabled') {
      return false;
    }

    if (selectionMode === 'enabled') {
      return this.showSelection();
    }

    return (
      this.showSelection() &&
      this.getEffectiveAssignmentModel() === 'coassess'
    );
  }

  getEffectiveShowDateMetadata(): boolean {
    const assignmentModel = this.getEffectiveAssignmentModel();
    if (assignmentModel !== 'coassess' && assignmentModel !== 'coreact') {
      return false;
    }

    return this.showDateMetadata();
  }

  onAssign(
    selected: (ProccessedCoassessCase | ProccessedCoreactCase)[],
    selectedCaseManagerId: string | null,
    selectedInvestigatorId: string | null
  ) {
    if (!selectedCaseManagerId && !selectedInvestigatorId) return;

    const isManagerAssign = !!selectedCaseManagerId;
    const userId = selectedCaseManagerId ?? selectedInvestigatorId!;

    const assign: AssignCase = {
      userId,
      caseIds: selected.map((c) => String(c.caseId)),
    };

    const bulkAssingInvSubscription = this.caseService
      .assignToCase(assign)
      .subscribe({
        next: () => {
          this.getToastMessage(true);
        },
        error: (err) => {
          this.getToastMessage(false);
        },
        complete: () => {
          const name =
            (isManagerAssign
              ? this.caseManagerOptions().find((o) => o.value === userId)?.label
              : this.investigationOptions().find((o) => o.value === userId)
                  ?.label) ?? userId;

          const selectedIds = new Set(selected.map((s) => s.caseId));
          for (const r of this.rows) {
            if (!selectedIds.has(r.caseId)) continue;

            if (isManagerAssign) {
              (r as any).caseManager = name;
            } else {
              (r as any).investigator = name;
              (r as any).investigationResults = status.UNDER_INVESTIGATION;
            }
          }
          this.rows = [...this.rows];
          this.onRowsChange.emit(this.rows);
          this.selectedCaseManagerMap = {};
          this.selectedInvestigatorMap = {};
          this.clearSelectedCases();
          this.cdr.markForCheck();
        },
      });

    this.subscriptions.push(bulkAssingInvSubscription);
  }
  clearSelectedCases() {
    this.selected = [];
    this.selectedCaseManagerId = '';
    this.selectedInvestigatorId = '';
  }
  getToastMessage(isSuccess: boolean) {
    if (isSuccess) {
      this.messageService.add({
        severity: 'success',
        summary: 'Success!',
        detail: this.translate.instant('TOAST.ASSIGN_SUCCESS'),
      });
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error!',
        detail: 'Error assigning to case!',
      });
    }
  }


  onDetailToggle(event: { value: any }) {
    const row = event.value;
    // this.toggledRow.emit(row);
    const patchCase: IPatchCase[] = [
      {
        op: 'replace',
        path: '/isNewCase',
        value: false,
      },
    ];
    if (event.value.isNewCase == true) {
      const markAsOldCaseSubscription$ = this.caseService
        .patchCase(row.caseId, patchCase)
        .subscribe({
          next: (value) => {
            // debugger;
          },
          error(err) {},
          complete: () => {},
        });

      this.subscriptions.push(markAsOldCaseSubscription$);
    }
  }

  navigateToCaseDetails(row: any) {
    const detailId = row?.[this.getEffectiveDetailIdField()];
    if (!detailId) {
      return;
    }

    this.router.navigate([this.getEffectiveDetailRoute(), detailId]);
  }

  getPriorityBadge(severityIndex: number) {
    let badgeClass = 'badge-secondary';

    if (severityIndex <= 0.4) {
      badgeClass = 'badge-success';
    } else if (severityIndex <= 0.95) {
      badgeClass = 'badge-warning';
    } else {
      badgeClass = 'badge-danger';
    }

    // return 'badge w-50 p-[0.75rem] self-center badge-square badge-lg ' + badgeClass;
    return 'badge badge-square w-6 h-6 p-0 ' + badgeClass;
  }

  getBooleanBadge(value: boolean | string | number): string {
    let badgeClass = 'badge-secondary';
    if (value === true || value === 'true' || value === 1) {
      badgeClass = 'badge-success'; // true → green
    } else if (value === false || value === 'false' || value === 0) {
      badgeClass = 'badge-danger'; // false → red
    }

    return (
      'badge badge-square w-6 h-6 p-0 flex items-center justify-center ' +
      badgeClass
    );
  }

  getBooleanBadgeIcon(value: boolean | string | number): string {
    if (value === true || value === 'true' || value === 1) {
      return 'fa fa-check';
    } else if (value === false || value === 'false' || value === 0) {
      return 'fa fa-times';
    }
    return '';
  }

  getTriagingBadge(value: string): string {
    //console.log('Triaging value:', value);
    let badgeClass = 'badge-secondary';
    if (value.toUpperCase() === 'NOSEVERITY') {
      badgeClass = 'badge-success'; // NoSeverity → green
    }
    if (value.toUpperCase() === 'LOWSEVERITY') {
      badgeClass = 'badge-warning'; // LowSeverity → blue
    }
    if (value.toUpperCase() === 'MEDIUMSEVERITY') {
      badgeClass = 'badge-orange'; // MediumSeverity → orange
    }
    if (value.toUpperCase() === 'HIGHSEVERITY') {
      badgeClass = 'badge-danger'; // HighSeverity → red
    }
    return (
      'badge badge-square w-6 h-6 p-0 flex items-center justify-center ' +
      badgeClass
    );
  }

  getSeverityBadge(severity: string) {
    let badgeClass: string;

    switch (severity.toLowerCase()) {
      case 'low':
        badgeClass = ' badge-success';
        break;
      case 'medium':
        badgeClass = 'badge-warning';
        break;
      case 'high':
        badgeClass = 'badge-danger';
        break;
      default:
        badgeClass = 'badge-secondary'; // default case if severity doesn't match
    }

    return (
      'badge w-50 p-[0.75rem] self-center badge-square badge-lg ' + badgeClass
    );
  }
  getToolTip(prop: string) {
    if (prop == 'Image Similarity Score') {
      return 'Identification and analysis of image similarities from vehicle images of past accidents';
    } else if (prop == 'Rule-Based System') {
      return 'The alert scores are generated from a combination of data-driven analysis and empirical rules, providing a balanced assessment based on both statistical insights and expert knowledge.';
    } else if (prop == 'Pre-Claim Anticipation') {
      return ' A predictive system that estimates potential claim amounts and identifies likely issues before the actual billing occurs, helping to streamline the claims process and mitigate risks early on.';
    } else if (prop == 'Price Anomaly Detection') {
      return 'Identifies and flags overcharges in medical expenses, highlighting instances where costs exceed expected or standard rates.';
    } else if (prop == 'Expenses Profile') {
      return 'The expense profile model identifies anomalies in the percentage distribution of medical expenses, flagging unusual patterns or deviations from typical spending trends.';
    }
    return '';
  }
  onLazyLoad(event: any) {
    const rows = event.rows ?? this.pageSize();
    const first = event.first ?? 0;
    const pageOneBased = Math.floor(first / rows) + 1;
    let requestTriggered = false;

    if (!this.initialized) {
      this.initialized = true;
      return;
    }

    if (event.sortField) {
      const sortBy = event.sortField;
      const sortDesc = (event.sortOrder ?? 1) === -1;
      const sortChanged =
        !!sortBy &&
        (sortBy !== this.sortBy() || sortDesc !== this.sortDesc());

      if (sortChanged) {
        this.isTableLoading = true;
        this.onSortChange.emit({ sortBy, sortDesc });
        requestTriggered = true;
      }
    }

    if (pageOneBased !== this.pageNumber()) {
      this.isTableLoading = true;
      this.onPageChange.emit(pageOneBased);
      requestTriggered = true;
    }

    if (requestTriggered) {
      this.rows = [];
      this.selected = [];
    }

    this.cdr.markForCheck();
  }

  onPrimeSelectionChange(selectedRows: any[]) {
    this.selected = selectedRows ?? [];
  }

  onPrimeRowExpand(event: any) {
    this.onDetailToggle({ value: event.data });
  }

  onPrimeRowCollapse(event: any) {
    if (event?.data) {
      (event.data as any).expanded = false;
    }
  }
}
